import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2 } from 'lucide-react';

import useAdminOrders, { ORDER_STATUSES, PAYMENT_STATUSES } from '../../hooks/useAdminOrders';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import OrderStatusBadge from '../../components/order/OrderStatusBadge';
import { parseApiError } from '../../utils/apiError';

// Dedicated workflow view for local-pickup orders — always requiresPickup=true
// (unlike the general Orders page, this isn't a toggle here, it's the point of
// the page). Same free-choice status editing as the Ship page — picking
// 'picked_up' from the dropdown is how an order gets marked picked up.
export default function AdminPickups() {
  const { addToast } = useToast();
  const { confirm, dialog } = useConfirm();
  const [status, setStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [page, setPage] = useState(1);
  const [savingId, setSavingId] = useState(null);

  const { items, total, pages, loading, updateStatus, deleteOrder } = useAdminOrders({
    status,
    paymentStatus,
    requiresPickup: true,
    page,
    limit: 20,
  });

  const onStatusChange = async (order, next) => {
    if (next === order.status) return;
    setSavingId(order._id);
    try {
      await updateStatus(order._id, next);
      addToast(`Order ${order.orderNumber} → ${next}`, { variant: 'success' });
    } catch (err) {
      const { formError, fieldErrors } = parseApiError(err);
      addToast(fieldErrors.status || formError || 'Could not update status', { variant: 'error' });
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (order) => {
    const ok = await confirm({
      title: 'Delete order',
      message: `Delete order ${order.orderNumber}? It can be restored later from Deleted orders.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteOrder(order._id);
      addToast(`Order ${order.orderNumber} deleted`, { variant: 'success' });
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not delete order', { variant: 'error' });
    }
  };

  const setFilter = (s) => {
    setStatus(s);
    setPage(1);
  };

  const setPaymentFilter = (s) => {
    setPaymentStatus(s);
    setPage(1);
  };

  // 'shipped' is a shipping-fulfillment status — pickup orders never go through it.
  const pickupStatuses = ORDER_STATUSES.filter((s) => s !== 'shipped');
  // Unlike the Ship page's dropdown, 'picked_up' stays in the option list here —
  // it's how an order gets marked picked up on this page.

  const filterClauses = [];
  if (status) filterClauses.push(`with status "${status.replace(/_/g, ' ')}"`);
  if (paymentStatus) filterClauses.push(`with payment "${paymentStatus.replace(/_/g, ' ')}"`);
  const emptyMessage = filterClauses.length ? `No pickup orders ${filterClauses.join(' and ')}` : 'No pickup orders';

  return (
    <div>
      <h1>Pickups</h1>
      <p style={{ color: 'var(--ss-brown-hover)', marginTop: 'var(--ss-space-1)' }}>
        Pickup orders, filterable by fulfillment status and payment status.
      </p>

      <span className="ss-label" style={{ display: 'block', marginTop: 'var(--ss-space-4)' }}>Fulfillment</span>
      <div className="ss-admin-filters" role="group" aria-label="Filter by fulfillment status" style={{ marginTop: 'var(--ss-space-2)' }}>
        <button type="button" className={`ss-chip ${status === '' ? 'is-active' : ''}`} onClick={() => setFilter('')}>All</button>
        {pickupStatuses.map((s) => (
          <button key={s} type="button" className={`ss-chip ${status === s ? 'is-active' : ''}`} onClick={() => setFilter(s)}>{s.replace(/_/g, ' ')}</button>
        ))}
      </div>

      <span className="ss-label" style={{ display: 'block', marginTop: 'var(--ss-space-4)' }}>Payment</span>
      <div className="ss-admin-filters" role="group" aria-label="Filter by payment status" style={{ marginTop: 'var(--ss-space-2)' }}>
        <button type="button" className={`ss-chip ${paymentStatus === '' ? 'is-active' : ''}`} onClick={() => setPaymentFilter('')}>All</button>
        {PAYMENT_STATUSES.map((s) => (
          <button key={s} type="button" className={`ss-chip ${paymentStatus === s ? 'is-active' : ''}`} onClick={() => setPaymentFilter(s)}>{s.replace(/_/g, ' ')}</button>
        ))}
      </div>

      {loading ? (
        <div className="ss-state"><Spinner label="Loading pickups" size={32} /></div>
      ) : items.length === 0 ? (
        <div className="ss-state">
          <h2>{emptyMessage}</h2>
        </div>
      ) : (
        <>
          <Card className="ss-admin-table-wrap" style={{ marginTop: 'var(--ss-space-5)', padding: 0 }}>
            <div className="ss-table-scroll">
            <table className="ss-admin-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th style={{ textAlign: 'center' }}>Payment</th>
                  <th style={{ textAlign: 'center' }}>Fulfillment</th>
                  <th aria-label="Change status" />
                </tr>
              </thead>
              <tbody>
                {items.map((o) => {
                  const statusOptions = pickupStatuses.includes(o.status)
                    ? pickupStatuses
                    : [o.status, ...pickupStatuses];
                  return (
                    <tr key={o._id}>
                      <td className="ss-admin-table__title">
                        <Link to={`/orders/${o.orderNumber}`} state={{ from: '/admin/pickups', fromLabel: 'Pickups' }}>{o.orderNumber}</Link>
                      </td>
                      <td style={{ textAlign: 'center' }}><OrderStatusBadge status={o.paymentStatus} /></td>
                      <td style={{ textAlign: 'center' }}><OrderStatusBadge status={o.status} /></td>
                      <td>
                        <div className="ss-admin-actions">
                          <select
                            className="ss-input ss-input--sm"
                            value={o.status}
                            disabled={savingId === o._id}
                            onChange={(e) => onStatusChange(o, e.target.value)}
                            aria-label={`Set status for ${o.orderNumber}`}
                          >
                            {statusOptions.map((s) => (
                              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                            ))}
                          </select>
                          {savingId === o._id && <Spinner label="Saving" />}
                          <button type="button" className="ss-icon-btn ss-icon-btn--danger" onClick={() => onDelete(o)} aria-label={`Delete order ${o.orderNumber}`}>
                            <Trash2 size={16} aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </Card>

          <div className="ss-admin-cards" style={{ marginTop: 'var(--ss-space-5)' }}>
            {items.map((o) => {
              const statusOptions = pickupStatuses.includes(o.status)
                ? pickupStatuses
                : [o.status, ...pickupStatuses];
              return (
                <div className="ss-admin-card" key={o._id}>
                  <div className="ss-admin-card__row">
                    <Link to={`/orders/${o.orderNumber}`} state={{ from: '/admin/pickups', fromLabel: 'Pickups' }} className="ss-admin-card__title">{o.orderNumber}</Link>
                  </div>
                  <div className="ss-admin-card__row">
                    <span className="ss-admin-card__label">Payment</span>
                    <OrderStatusBadge status={o.paymentStatus} />
                  </div>
                  <div className="ss-admin-card__row">
                    <span className="ss-admin-card__label">Fulfillment</span>
                    <OrderStatusBadge status={o.status} />
                  </div>
                  <div className="ss-admin-card__row">
                    <span className="ss-admin-card__label">Set status</span>
                    <select
                      className="ss-input ss-input--sm"
                      value={o.status}
                      disabled={savingId === o._id}
                      onChange={(e) => onStatusChange(o, e.target.value)}
                      aria-label={`Set status for ${o.orderNumber}`}
                    >
                      {statusOptions.map((s) => (
                        <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                    {savingId === o._id && <Spinner label="Saving" />}
                    <button type="button" className="ss-icon-btn ss-icon-btn--danger" onClick={() => onDelete(o)} aria-label={`Delete order ${o.orderNumber}`}>
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {pages > 1 && (
        <div className="ss-pager" style={{ marginTop: 'var(--ss-space-4)' }}>
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>Previous</Button>
          <span className="ss-pager__info">Page {page} of {pages} · {total} total</span>
          <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage((n) => n + 1)}>Next</Button>
        </div>
      )}

      {dialog}
    </div>
  );
}
